import axios, { AxiosRequestConfig, Method as AxiosMethod } from 'axios';
import {
  useQueryClient,
  QueryKey,
  FetchQueryOptions,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { addBreadcrumb } from '@/lib/sentry';
import { ApiError, extractApiError } from '@/lib/errors';
import { ACCOUNT_NOT_ACTIVE_REASON } from '@/lib/accountActivation';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Re-read the session when the backend refuses it for not being active. */
const noteAccountNotActive = (apiError: ApiError): void => {
  if (apiError.status === 403 && apiError.details?.reason === ACCOUNT_NOT_ACTIVE_REASON) {
    void useAuthStore.getState().handleAccountNotActive();
  }
};

const callApi = async <T = unknown>(
  url: string,
  config: AxiosRequestConfig = {},
  handleAuthRedirect?: (currentPath: string, error?: any) => void
): Promise<T> => {
  const fullUrl = url.startsWith('http') ? url : `${API_URL}/api${url}`;
  const effectiveConfig = { ...config, withCredentials: true };
  const method = effectiveConfig.method?.toUpperCase() || 'GET';

  if (method === 'GET' && effectiveConfig.data) {
    effectiveConfig.params = { ...(effectiveConfig.params || {}), ...effectiveConfig.data };
    delete effectiveConfig.data;
  }

  // Add Sentry breadcrumb for API call
  addBreadcrumb('api', `${method} ${url}`, {
    url: fullUrl,
    method,
  });

  try {
    const response = await axios({ url: fullUrl, ...effectiveConfig });
    return response.data;
  } catch (error) {
    // Let auth store handle 401 errors and redirect logic
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (handleAuthRedirect) {
        handleAuthRedirect(window.location.pathname, error);
      }
    }
    const apiError = extractApiError(error);
    noteAccountNotActive(apiError);
    throw apiError;
  }
};

// Export a version that returns the full response for cases where headers are needed
export const callApiWithResponse = async <T = unknown>(
  url: string,
  config: AxiosRequestConfig = {},
  handleAuthRedirect?: (currentPath: string, error?: any) => void
) => {
  const fullUrl = url.startsWith('http') ? url : `${API_URL}/api${url}`;
  const effectiveConfig = { ...config, withCredentials: true };

  if (effectiveConfig.method?.toUpperCase() === 'GET' && effectiveConfig.data) {
    effectiveConfig.params = { ...(effectiveConfig.params || {}), ...effectiveConfig.data };
    delete effectiveConfig.data;
  }

  try {
    return await axios<T>({ url: fullUrl, ...effectiveConfig });
  } catch (error) {
    // Let auth store handle 401 errors and redirect logic
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (handleAuthRedirect) {
        handleAuthRedirect(window.location.pathname, error);
      }
    }
    const apiError = extractApiError(error);
    noteAccountNotActive(apiError);
    throw apiError;
  }
};

/**
 * Parameters for the imperative doFetch method (legacy signature).
 */
export interface DoFetchParams<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
> {
  method?: AxiosMethod;
  queryKey?: TQueryKey;
  data?: unknown;
  axiosConfig?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data' | 'params'>;
  queryKeyToInvalidate?: QueryKey | QueryKey[];
  fetchQueryOptions?: Omit<FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
}

/**
 * Internal configuration object for the core doFetch logic.
 */
interface InternalDoFetchConfig<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
> extends DoFetchParams<TQueryFnData, TError, TData, TQueryKey> {
  url: string; // URL is now part of this internal config
}


export interface StreamCallbacks {
  onMessage?: (data: unknown) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
  onOpen?: () => void;
}

export interface UseFetchReturn {
  doFetch: <
    TData = unknown, // Expected data type from the API call
    TError = Error,
    TQueryFnData = TData, // Data type queryFn returns for fetchQuery; defaults to TData
    TQueryKey extends QueryKey = QueryKey
  >(
    url: string, // Kept the original signature: url as first param
    params?: DoFetchParams<TQueryFnData, TError, TData, TQueryKey> // Optional config object
  ) => Promise<TData>;
  
  doFetchStream: (
    url: string,
    callbacks: StreamCallbacks,
    options?: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      data?: unknown;
      queryKeyToInvalidate?: QueryKey | QueryKey[];
    }
  ) => EventSource;
}

function useFetch(): UseFetchReturn {
  const queryClient = useQueryClient();
  const handleAuthRedirect = useAuthStore((state) => state.handleAuthRedirect);

  const doFetchCore = async <
    TData = unknown,
    TError = Error,
    TQueryFnData = TData, // Default TQueryFnData to TData
    TQueryKey extends QueryKey = QueryKey
  >(
    config: InternalDoFetchConfig<TQueryFnData, TError, TData, TQueryKey>
  ): Promise<TData> => {
    const {
      url,
      method = 'GET', // Default method to GET
      queryKey,
      data,
      axiosConfig = {},
      queryKeyToInvalidate,
      fetchQueryOptions = {},
    } = config;

    let responseData: TData;
    const upperCaseMethod = method.toUpperCase();

    const baseAxiosConfig: AxiosRequestConfig = {
      ...axiosConfig,
      method: method as AxiosMethod,
      data: data,
    };

    if (upperCaseMethod === 'GET') {
      if (!queryKey) {
        // console.warn('[useFetch().doFetchCore] GET request to', url, 'made without a queryKey.');
        responseData = await callApi<TData>(url, baseAxiosConfig, handleAuthRedirect);
      } else {
        // console.log('[useFetch().doFetchCore] GET request to', url, 'using fetchQuery with key:', queryKey);
        // Ensure TData is compatible with TQueryFnData for fetchQuery's queryFn
        responseData = await queryClient.fetchQuery<TQueryFnData, TError, TData, TQueryKey>({
          queryKey: queryKey,
          queryFn: () => callApi<TQueryFnData>(url, baseAxiosConfig, handleAuthRedirect), // queryFn returns TQueryFnData
          ...fetchQueryOptions,
        });
      }
    } else {
      // console.log(`[useFetch().doFetchCore] ${upperCaseMethod} request to ${url}`);
      responseData = await callApi<TData>(url, baseAxiosConfig, handleAuthRedirect);
    }

    if (queryKeyToInvalidate) {
      let finalKeysForInvalidation: QueryKey[] = [];
      if (Array.isArray(queryKeyToInvalidate) && queryKeyToInvalidate.every(Array.isArray)) {
        finalKeysForInvalidation = queryKeyToInvalidate as QueryKey[];
      } else if (Array.isArray(queryKeyToInvalidate)) {
        finalKeysForInvalidation = [queryKeyToInvalidate as QueryKey];
      }

      if (finalKeysForInvalidation.length > 0) {
        // console.log(`[useFetch().doFetchCore] Invalidating keys for ${upperCaseMethod} ${url}:`, finalKeysForInvalidation);
        await Promise.all(
          finalKeysForInvalidation.map(key => queryClient.invalidateQueries({ queryKey: key }))
        );
      }
    }
    return responseData;
  };

  // Adapter function to match the old doFetch(url, params?) signature
  const doFetchAdapter = <
    TData = unknown,
    TError = Error,
    TQueryFnData = TData,
    TQueryKey extends QueryKey = QueryKey
  >(
    url: string,
    params?: DoFetchParams<TQueryFnData, TError, TData, TQueryKey>
  ): Promise<TData> => {
    const internalConfig: InternalDoFetchConfig<TQueryFnData, TError, TData, TQueryKey> = {
      url,
      ...(params || {}), // Spread the optional params
    };
    return doFetchCore(internalConfig);
  };

  const doFetchStream = (
    url: string,
    callbacks: StreamCallbacks,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      data?: unknown;
      queryKeyToInvalidate?: QueryKey | QueryKey[];
    } = {}
  ): EventSource => {
    const { method = 'GET', data, queryKeyToInvalidate } = options;
    
    // Build the full URL - same logic as callApi
    const fullUrl = url.startsWith('http') ? url : `${API_URL}/api${url}`;
    
    // For POST/PUT/DELETE with data, append as query params since EventSource only supports GET
    let finalUrl = fullUrl;
    if (data) {
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          params.append(key, String(value));
        }
      });
      const separator = fullUrl.includes('?') ? '&' : '?';
      finalUrl = `${fullUrl}${separator}${params.toString()}`;
    }

    const eventSource = new EventSource(finalUrl);

    eventSource.onopen = () => {
      callbacks.onOpen?.();
    };
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        callbacks.onMessage?.(data);
        
        // Handle completion
        if (data.type === 'complete' || data.type === 'finished') {
          callbacks.onComplete?.();
          eventSource.close();
          
          // Invalidate queries if specified
          if (queryKeyToInvalidate) {
            let finalKeysForInvalidation: QueryKey[] = [];
            if (Array.isArray(queryKeyToInvalidate) && queryKeyToInvalidate.every(Array.isArray)) {
              finalKeysForInvalidation = queryKeyToInvalidate as QueryKey[];
            } else if (Array.isArray(queryKeyToInvalidate)) {
              finalKeysForInvalidation = [queryKeyToInvalidate as QueryKey];
            }

            if (finalKeysForInvalidation.length > 0) {
              finalKeysForInvalidation.forEach(key => 
                queryClient.invalidateQueries({ queryKey: key })
              );
            }
          }
        }
      } catch (error) {
        callbacks.onError?.(error as Error);
      }
    };

    eventSource.onerror = (error) => {
      callbacks.onError?.(error as Error);
      eventSource.close();
    };
    
    return eventSource;
  };

  return { doFetch: doFetchAdapter, doFetchStream };
}

// Export callApi for use in stores and other non-component contexts
export { callApi };

export default useFetch;